import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboardStats, useNetworkEvents, useRecentActivity } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { downloadPdfReport } from '../lib/pdfReport';

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const { data: stats, loading: statsLoading, error: statsError } = useDashboardStats();
  const { data: events, loading: eventsLoading, error: eventsError } = useNetworkEvents();
  const { data: recentActivity, loading: activityLoading, error: activityError } = useRecentActivity();

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">
            {t('welcome', { name: user?.name ?? t('fallbackName') })}
          </h1>
          <p className="mt-1 text-sm text-text-muted">{t('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            className="btn-secondary"
            onClick={() => {
              // Everything here is already scoped to the caller by the API: the
              // stats and activity are their own, network events are public.
              const when = (iso: string) => (iso && !Number.isNaN(new Date(iso).getTime()) ? new Date(iso).toLocaleString() : iso);
              const now = new Date();
              void downloadPdfReport({
                title: 'IDA Dashboard Report',
                meta: [
                  `Prepared for ${user?.name ?? ''}${user?.email ? ` (${user.email})` : ''}${user?.role ? ` — ${user.role}` : ''}`,
                  `Generated ${now.toLocaleString()}`,
                ],
                sections: [
                  {
                    heading: 'Summary',
                    columns: ['Metric', 'Value'],
                    rows: (stats ?? []).map((s) => [s.label, s.value]),
                  },
                  {
                    heading: 'Recent Activity',
                    columns: ['Time', 'Action', 'Detail'],
                    rows: (recentActivity ?? []).map((a) => [when(a.timestamp), a.action, a.detail]),
                    emptyText: 'No recent activity.',
                  },
                  {
                    heading: 'Recent Network Events',
                    columns: ['Time', 'Event', 'Identifier'],
                    rows: (events ?? []).map((e) => [when(e.timestamp), e.label, e.value]),
                    emptyText: 'No network events.',
                  },
                ],
                fileName: `ida-dashboard-report-${now.toISOString().slice(0, 10)}.pdf`,
              });
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
            {t('exportData')}
          </button>
          {/* Admins do not register issuers themselves — they review applications.
              hasRole('issuer') is true for admins, so without this branch an admin
              was sent to the self-service onboarding form, which the API rejects
              (only holders may apply). Send them to the review queue instead. */}
          {hasRole('admin') ? (
            <button className="btn-primary" onClick={() => navigate('/admin/role-applications')}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>manage_accounts</span>
              Review Role Applications
            </button>
          ) : hasRole('issuer') ? (
            <button className="btn-primary" onClick={() => navigate('/issuer/onboarding')}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
              {t('registerIssuer')}
            </button>
          ) : null}
          {user?.role === 'holder' && (
            <button className="btn-secondary" onClick={() => navigate('/role-application')}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>upgrade</span>
              Apply for Role
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {statsLoading ? (
        // Fixed-height skeletons that match the loaded stat cards (h-32) so the
        // layout doesn't shift when data arrives.
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="stat-card h-32 animate-pulse">
              <div className="h-3 w-24 rounded bg-slate-200"></div>
              <div className="mt-auto">
                <div className="h-8 w-20 rounded bg-slate-200"></div>
                <div className="mt-2 h-3 w-32 rounded bg-slate-100"></div>
              </div>
            </div>
          ))}
        </div>
      ) : statsError ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadStatsFailed')}</div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {(Array.isArray(stats) ? stats : []).map((stat) => (
            <div key={stat.label} className="stat-card group h-32">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                  {stat.label}
                </span>
                {/* Icon and colour follow the trend's direction. Previously
                    every card showed a green up-arrow, including for -20% and
                    for "0%" — and "0-to-n" was reported as +100%, which reads
                    as growth. The API now says whether a percentage is even
                    defined; "new" and "—" are shown in neutral tones. */}
                {stat.direction !== 'none' && (
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs font-bold ${
                      stat.direction === 'up'   ? 'bg-emerald-50 text-emerald-600'
                    : stat.direction === 'down' ? 'bg-red-50 text-red-600'
                    : stat.direction === 'new'  ? 'bg-sky-50 text-sky-700'
                    :                             'bg-slate-100 text-text-muted'
                    }`}
                    title={
                      stat.direction === 'new'
                        ? 'New in the last 7 days — there was nothing in the 7 days before, so no percentage can be calculated'
                        : stat.direction === 'flat'
                          ? 'No change versus the previous 7 days'
                          : 'Change over the last 7 days, compared with the 7 days before'
                    }
                  >
                    <span className="material-symbols-outlined mr-0.5 align-middle" style={{ fontSize: 12 }}>
                      {stat.direction === 'up' ? 'trending_up'
                      : stat.direction === 'down' ? 'trending_down'
                      : stat.direction === 'new' ? 'fiber_new'
                      : 'trending_flat'}
                    </span>
                    {stat.trend}
                  </span>
                )}
              </div>
              <div className="mt-auto">
                <span className="text-3xl font-bold text-text-main">{stat.value}</span>
                {stat.trendNote && <p className="mt-1 text-xs text-text-faint">{stat.trendNote}</p>}
              </div>
              <span
                className="material-symbols-outlined absolute -right-2 -top-2 text-8xl text-slate-900 opacity-5 transition-opacity group-hover:opacity-10"
                style={{ fontSize: 96, fontVariationSettings: "'FILL' 1" }}
              >
                {stat.icon}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>history</span>
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('recentActivity')}</h2>
            </div>
            {/* Every role, admins included, sees only their own actions here. */}
            <p className="mt-1 text-xs text-text-faint">Your most recent actions.</p>
          </div>
          {activityLoading ? (
            // Skeleton rows matching the loaded activity list, so no reflow.
            <div className="rounded-xl border border-border-subtle bg-white shadow-sm">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`flex items-start gap-4 p-4 ${i < 3 ? 'border-b border-border-subtle' : ''}`}>
                  <div className="size-10 shrink-0 animate-pulse rounded-lg bg-slate-200"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200"></div>
                    <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : activityError ? (
            <div className="text-center py-20 text-red-500">{t('common:errors.loadActivityFailed')}</div>
          ) : (
            <div className="rounded-xl border border-border-subtle bg-white shadow-sm">
              {(Array.isArray(recentActivity) ? recentActivity : []).length === 0 && (
                <p className="text-sm text-text-muted py-4 px-4">{t('common:empty.noRecentActivity')}</p>
              )}
              {(Array.isArray(recentActivity) ? recentActivity : []).map((activity, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-4 p-4 transition-colors hover:bg-slate-50/60 ${
                    idx < (Array.isArray(recentActivity) ? recentActivity : []).length - 1 ? 'border-b border-border-subtle' : ''
                  }`}
                >
                  <div className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${activity.iconBg}`}>
                    <span className={`material-symbols-outlined ${activity.iconColor}`} style={{ fontSize: 20 }}>{activity.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-text-main">{activity.action}</p>
                    <p className="mt-0.5 truncate text-xs text-text-faint" title={activity.detail}>{activity.detail}</p>
                  </div>
                  <span className="shrink-0 text-[10px] font-bold text-text-faint">{activity.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>bolt</span>
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('quickActions')}</h2>
          </div>
          <div className="space-y-3">
            {[
              { label: t('actions.createDid.label'), icon: 'badge', desc: t('actions.createDid.desc'), path: '/my-dids' },
              ...(hasRole('issuer') ? [{ label: t('actions.issueCredential.label'), icon: 'verified', desc: t('actions.issueCredential.desc'), path: '/issuer/issue' }] : []),
              ...(hasRole('verifier') ? [{ label: t('actions.verifyCredential.label'), icon: 'fact_check', desc: t('actions.verifyCredential.desc'), path: '/verifier/verify' }] : []),
              { label: t('actions.deployAgent.label'), icon: 'smart_toy', desc: t('actions.deployAgent.desc'), path: '/ai-agents/deploy' },
            ].map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className="flex w-full items-center gap-3 rounded-xl border border-border-subtle bg-white p-4 text-left transition-shadow hover:shadow-md"
              >
                <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>{action.icon}</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-text-main">{action.label}</p>
                  <p className="text-[10px] text-text-faint">{action.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>broadcast_on_personal</span>
          <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('recentEvents')}</h2>
        </div>
        {eventsLoading ? (
          // Skeleton event cards matching the loaded grid, so no reflow.
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-border-subtle bg-white p-4">
                <div className="mb-2 h-3 w-20 animate-pulse rounded bg-slate-200"></div>
                <div className="h-4 w-full animate-pulse rounded bg-slate-100"></div>
                <div className="mt-2 h-3 w-16 animate-pulse rounded bg-slate-100"></div>
              </div>
            ))}
          </div>
        ) : eventsError ? (
          <div className="text-center py-20 text-red-500">{t('common:errors.loadEventsFailed')}</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {(Array.isArray(events) ? events : []).map((event) => (
              <div key={`${event.label}-${event.id}`} className="rounded-xl border border-border-subtle bg-white p-4 transition-shadow hover:shadow-md">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-text-faint">
                    {event.label}
                  </span>
                  <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 16 }}>
                    {event.icon}
                  </span>
                </div>
                <p className="truncate font-mono text-xs font-bold text-text-main" title={event.value}>{event.value}</p>
                <div className="mt-2 flex items-center gap-1">
                  <span className="size-1.5 rounded-full bg-emerald-500"></span>
                  <span className="text-[10px] font-bold text-emerald-600" title={event.time}>{event.time}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
